import React from 'react';

export interface CalculatorCardItem {
  id: string;
  tab: string;
  path: string;
  title: string;
  badge: string;
  badgeClass: string;
  cardBg: string;
  borderClass: string;
  shadowClass: string;
  glowGradient: string;
  topBarGradient: string;
  iconGradient: string;
  iconShadow: string;
  titleHover: string;
  ctaColor: string;
  description: string;
  highlight: string;
  ctaText: string;
  iconSvg: React.ReactNode;
}

interface ExploreCalculatorsSectionProps {
  onNavigate: (path: string, tab: string) => void;
  title?: string;
  subtitle?: string;
  filterOutTab?: string;
}

export const ExploreCalculatorsSection: React.FC<ExploreCalculatorsSectionProps> = ({
  onNavigate,
  title = 'Explore More Calculators',
  subtitle = 'Dedicated hydraulic engineering modules for specialized pipe sizing and standards verification',
  filterOutTab,
}) => {
  const cards: CalculatorCardItem[] = [
    {
      id: 'copper',
      tab: 'copper',
      path: '/copper-pipe-sizing/',
      title: 'Copper Pipe Sizing',
      badge: 'BS EN 1057 • ASTM B88',
      badgeClass:
        'bg-amber-500/10 dark:bg-amber-400/15 text-amber-950 dark:text-amber-200 border-amber-500/30 dark:border-amber-400/30',
      cardBg:
        'bg-gradient-to-br from-white/85 via-amber-50/40 to-orange-50/20 dark:from-slate-900/80 dark:via-amber-950/25 dark:to-slate-900/85',
      borderClass:
        'border-amber-500/25 hover:border-amber-500/60 dark:border-amber-400/20 dark:hover:border-amber-400/50',
      shadowClass:
        'shadow-[0_8px_32px_0_rgba(217,119,6,0.08)] hover:shadow-[0_16px_48px_0_rgba(217,119,6,0.22)]',
      glowGradient:
        'from-amber-400/30 via-orange-500/25 to-transparent',
      topBarGradient:
        'from-amber-500 via-orange-400 to-amber-600',
      iconGradient:
        'from-amber-500 via-orange-500 to-amber-700',
      iconShadow:
        'shadow-orange-500/30',
      titleHover:
        'group-hover:text-amber-600 dark:group-hover:text-amber-400',
      ctaColor:
        'text-amber-600 dark:text-amber-400',
      description:
        'Size copper domestic hot & cold water, heating, and chilled lines with CIBSE acoustic & erosion velocity checks.',
      highlight: '15mm – 54mm Table X • CIBSE Limits',
      ctaText: 'Launch Copper Sizing',
      iconSvg: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      id: 'flow',
      tab: 'flow',
      path: '/flow-rate-calculator/',
      title: 'Flow Rate Calculator',
      badge: 'Multi-Unit Flow Engine',
      badgeClass:
        'bg-cyan-500/10 dark:bg-cyan-400/15 text-cyan-950 dark:text-cyan-200 border-cyan-500/30 dark:border-cyan-400/30',
      cardBg:
        'bg-gradient-to-br from-white/85 via-cyan-50/40 to-blue-50/20 dark:from-slate-900/80 dark:via-cyan-950/25 dark:to-slate-900/85',
      borderClass:
        'border-cyan-500/25 hover:border-cyan-400/60 dark:border-cyan-400/20 dark:hover:border-cyan-400/50',
      shadowClass:
        'shadow-[0_8px_32px_0_rgba(8,127,156,0.08)] hover:shadow-[0_16px_48px_0_rgba(57,191,211,0.22)]',
      glowGradient:
        'from-cyan-400/30 via-blue-500/25 to-transparent',
      topBarGradient:
        'from-[#087F9C] via-[#39BFD3] to-sky-400',
      iconGradient:
        'from-cyan-500 via-sky-500 to-[#087F9C]',
      iconShadow:
        'shadow-cyan-500/30',
      titleHover:
        'group-hover:text-[#087F9C] dark:group-hover:text-cyan-300',
      ctaColor:
        'text-[#087F9C] dark:text-cyan-400',
      description:
        'Convert pipe internal bore and fluid velocity or allowable pressure drop into L/s, m³/h, and US/UK GPM instantly.',
      highlight: 'Dual-Mode Solver • Simultaneous 4-Unit Output',
      ctaText: 'Launch Flow Rate',
      iconSvg: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      id: 'pressure',
      tab: 'pressure',
      path: '/pressure-drop-calculator/',
      title: 'Pressure Drop (Darcy)',
      badge: 'Swamee-Jain Head Loss',
      badgeClass:
        'bg-indigo-500/10 dark:bg-indigo-400/15 text-indigo-950 dark:text-indigo-200 border-indigo-500/30 dark:border-indigo-400/30',
      cardBg:
        'bg-gradient-to-br from-white/85 via-blue-50/40 to-violet-50/20 dark:from-slate-900/80 dark:via-indigo-950/25 dark:to-slate-900/85',
      borderClass:
        'border-indigo-500/25 hover:border-indigo-400/60 dark:border-indigo-400/20 dark:hover:border-indigo-400/50',
      shadowClass:
        'shadow-[0_8px_32px_0_rgba(79,70,229,0.08)] hover:shadow-[0_16px_48px_0_rgba(99,102,241,0.22)]',
      glowGradient:
        'from-blue-500/30 via-violet-500/25 to-transparent',
      topBarGradient:
        'from-blue-600 via-indigo-500 to-purple-600',
      iconGradient:
        'from-blue-600 via-indigo-600 to-purple-600',
      iconShadow:
        'shadow-indigo-500/30',
      titleHover:
        'group-hover:text-indigo-600 dark:group-hover:text-indigo-300',
      ctaColor:
        'text-indigo-600 dark:text-indigo-400',
      description:
        'Calculate Darcy friction factor, hydraulic gradient (kPa/100m), and total head loss across commercial pipes.',
      highlight: 'Explicit Friction Factor • Minor Losses (ΣK)',
      ctaText: 'Launch Pressure Drop',
      iconSvg: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: 'velocity',
      tab: 'velocity',
      path: '/pipe-velocity-standards/',
      title: 'Pipe Velocity & Standards',
      badge: 'CIBSE & BS EN 806 Limits',
      badgeClass:
        'bg-emerald-500/10 dark:bg-emerald-400/15 text-emerald-950 dark:text-emerald-200 border-emerald-500/30 dark:border-emerald-400/30',
      cardBg:
        'bg-gradient-to-br from-white/85 via-emerald-50/40 to-teal-50/20 dark:from-slate-900/80 dark:via-emerald-950/25 dark:to-slate-900/85',
      borderClass:
        'border-emerald-500/25 hover:border-emerald-400/60 dark:border-emerald-400/20 dark:hover:border-emerald-400/50',
      shadowClass:
        'shadow-[0_8px_32px_0_rgba(16,185,129,0.08)] hover:shadow-[0_16px_48px_0_rgba(16,185,129,0.22)]',
      glowGradient:
        'from-emerald-400/30 via-teal-500/25 to-transparent',
      topBarGradient:
        'from-emerald-500 via-teal-400 to-green-600',
      iconGradient:
        'from-emerald-500 via-teal-500 to-green-600',
      iconShadow:
        'shadow-emerald-500/30',
      titleHover:
        'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
      ctaColor:
        'text-emerald-600 dark:text-emerald-400',
      description:
        'Verify flow velocity, dynamic kinetic pressure, and velocity head against acoustic noise and erosion limits.',
      highlight: '0.5 – 2.4 m/s Guidance • Biofilm Prevention',
      ctaText: 'Launch Pipe Velocity',
      iconSvg: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
  ];

  const visibleCards = filterOutTab
    ? cards.filter((c) => c.tab !== filterOutTab)
    : cards;

  return (
    <section className="mt-12 pt-10 border-t border-slate-200/80 dark:border-slate-700/80">
      {/* Section Header */}
      <div className="mb-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#087F9C] dark:text-[#39BFD3] block mb-1">
            Specialized Engineering Modules
          </span>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#10283D] dark:text-white tracking-tight">
            {title}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>

      {/* 4-Card Responsive Grid (Desktop 4-col, Tablet 2-col, Mobile 1-col) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {visibleCards.map((card) => (
          <a
            key={card.id}
            href={card.path}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(card.path, card.tab);
            }}
            className={`group relative overflow-hidden rounded-3xl p-6 border backdrop-blur-[14px] ring-1 ring-inset ring-white/30 dark:ring-white/10 transition-all duration-300 ease-out flex flex-col justify-between cursor-pointer active:scale-[0.98] hover:-translate-y-2 hover:scale-[1.01] motion-reduce:transform-none motion-reduce:transition-none ${card.cardBg} ${card.borderClass} ${card.shadowClass}`}
          >
            {/* Top Slim Color Gradient Accent Bar */}
            <div
              className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.topBarGradient}`}
            />

            {/* Subtle Ambient Radial Glow in Top-Right Corner */}
            <div
              className={`absolute -top-14 -right-14 w-40 h-40 rounded-full bg-gradient-to-br ${card.glowGradient} blur-2xl pointer-events-none opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-500`}
            />

            {/* Glass Sheen Light Reflection Overlay */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-white/50 via-white/10 to-transparent dark:from-white/10 dark:via-transparent dark:to-transparent pointer-events-none" />

            <div className="relative z-10">
              {/* Card Header: 3D Tile Icon + Glass Badge */}
              <div className="flex items-center justify-between gap-2 mb-4">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 text-white shadow-md transition-all duration-300 group-hover:scale-110 group-hover:rotate-1 motion-reduce:transform-none bg-gradient-to-br ${card.iconGradient} ${card.iconShadow}`}
                >
                  {card.iconSvg}
                </div>

                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border backdrop-blur-md truncate max-w-[170px] ${card.badgeClass}`}
                >
                  {card.badge}
                </span>
              </div>

              {/* Title & Description */}
              <h3
                className={`font-display font-extrabold text-lg text-slate-900 dark:text-white transition-colors duration-200 ${card.titleHover}`}
              >
                {card.title}
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-2.5 leading-relaxed">
                {card.description}
              </p>
            </div>

            {/* Card Footer: Metadata Tag + Interactive Arrow CTA */}
            <div className="relative z-10 pt-4 mt-5 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 truncate">
                {card.highlight}
              </span>
              <span
                className={`text-xs font-bold flex items-center gap-1.5 shrink-0 transition-colors ${card.ctaColor}`}
              >
                <span>{card.ctaText}</span>
                <span className="text-sm transition-transform duration-200 group-hover:translate-x-1.5 motion-reduce:transform-none">
                  →
                </span>
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};
